import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HPButton from "../common/Button";
import { LineSortingOption } from "@store/slices/line-chart-config-slice";
import { getLineColor, CHART_COLORS } from "@utils/chart-utils";

interface LineItem {
  id: string;
  name: string;
}

interface LineChartCustomizationPanelProps {
  lines: LineItem[];
  hiddenLines: Record<string, boolean>;
  customLineLabels: Record<string, string>;
  lineOrder: string[];
  onToggleLineVisibility: (lineId: string, visible: boolean) => void;
  onUpdateLineLabel: (lineId: string, label: string) => void;
  onResetLineLabel: (lineId: string) => void;
  onShowAllLines: () => void;
  onHideAllLines: () => void;
  onReorderLines: (fromIndex: number, toIndex: number) => void;
  sortingOption: LineSortingOption;
  onUpdateSortingOption: (option: LineSortingOption) => void;
  colorPalette?: string;
  customColors?: Record<string, string>;
}

// Sortable Line Item Component
interface SortableLineItemProps {
  item: LineItem;
  isHidden: boolean;
  hasCustomLabel: boolean;
  displayLabel: string;
  isEditing: boolean;
  editValue: string;
  isDragEnabled: boolean;
  swatchColor?: string;
  onToggleVisibility: (id: string, visible: boolean) => void;
  onStartEdit: (item: LineItem) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onResetLabel: (id: string) => void;
  onEditValueChange: (value: string) => void;
}

const SortableLineItem: React.FC<SortableLineItemProps> = ({
  item,
  isHidden,
  hasCustomLabel,
  displayLabel,
  isEditing,
  editValue,
  isDragEnabled,
  swatchColor,
  onToggleVisibility,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onResetLabel,
  onEditValueChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !isDragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isHidden ? "#f5f5f5" : "white",
        opacity: isHidden ? 0.6 : 1,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Drag handle */}
      {isDragEnabled && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "#666",
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {/* Visibility toggle */}
      <IconButton
        size="small"
        onClick={() => onToggleVisibility(item.id, isHidden)}
        sx={{ flexShrink: 0 }}
      >
        {isHidden ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>

      {/* Label or edit field */}
      {isEditing ? (
        <Box sx={{ flex: 1 }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
            className="w-full px-3 py-2 border border-[#D9D9D9] rounded-lg text-sm outline-none focus:border-[#1976d2] transition-colors"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: hasCustomLabel ? 600 : 400,
            }}
          >
            {displayLabel}
          </Typography>
          {hasCustomLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              Original: {item.name}
            </Typography>
          )}
        </Box>
      )}

      {/* Action buttons */}
      {isEditing ? (
        <>
          <IconButton size="small" onClick={onSaveEdit} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={() => onStartEdit(item)}
            disabled={isHidden}
            sx={{ flexShrink: 0 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {swatchColor && !isHidden && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: swatchColor,
                flexShrink: 0,
                border: "1px solid rgba(0,0,0,0.12)",
              }}
            />
          )}
          {hasCustomLabel && (
            <HPButton
              size="small"
              variant="text"
              onClick={() => onResetLabel(item.id)}
            >
              Reset
            </HPButton>
          )}
        </>
      )}
    </Box>
  );
};

const LineChartCustomizationPanel: React.FC<
  LineChartCustomizationPanelProps
> = ({
  lines,
  hiddenLines,
  customLineLabels,
  lineOrder,
  onToggleLineVisibility,
  onUpdateLineLabel,
  onResetLineLabel,
  onShowAllLines,
  onHideAllLines,
  onReorderLines,
  sortingOption,
  onUpdateSortingOption,
  colorPalette,
  customColors,
}) => {
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLineValue, setEditLineValue] = useState("");

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle line drag end
  const handleLineDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = lineOrder.indexOf(active.id as string);
      const newIndex = lineOrder.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderLines(oldIndex, newIndex);
      }
    }
  };

  // Order lines based on lineOrder
  const orderedLines = useMemo(() => {
    // If lineOrder is not provided or empty, return lines as-is
    if (!lineOrder || lineOrder.length === 0) {
      return lines;
    }

    // Create a map for quick lookup
    const linesMap = new Map(lines.map((line) => [line.id, line]));

    // Order lines according to lineOrder
    const ordered = lineOrder
      .map((id) => linesMap.get(id))
      .filter((line): line is LineItem => line !== undefined);

    return ordered;
  }, [lines, lineOrder]);

  // Precompute swatch colors for Custom palette mode.
  // Index matches use-line-chart-options: position within visible ordered series.
  const lineSwatchColors = useMemo<Record<string, string> | undefined>(() => {
    if (colorPalette !== "Custom") return undefined;
    const result: Record<string, string> = {};
    let visibleIndex = 0;
    for (const line of orderedLines) {
      if (!line) continue;
      if (hiddenLines[line.id] !== false) {
        result[line.id] = getLineColor(
          visibleIndex,
          line.id,
          customColors ?? {},
          CHART_COLORS,
        );
        visibleIndex++;
      }
    }
    return result;
  }, [colorPalette, customColors, orderedLines, hiddenLines]);

  // Count visible items
  const visibleLinesCount = lines.filter(
    (l) => hiddenLines[l.id] !== false,
  ).length;

  // Handle line edit
  const handleStartEditLine = (line: LineItem) => {
    setEditingLineId(line.id);
    setEditLineValue(customLineLabels[line.id] || line.name);
  };

  const handleSaveLineEdit = () => {
    if (editingLineId && editLineValue.trim()) {
      onUpdateLineLabel(editingLineId, editLineValue.trim());
    }
    setEditingLineId(null);
    setEditLineValue("");
  };

  const handleCancelLineEdit = () => {
    setEditingLineId(null);
    setEditLineValue("");
  };

  const handleResetLineLabel = (id: string) => {
    onResetLineLabel(id);
    if (editingLineId === id) {
      setEditingLineId(null);
      setEditLineValue("");
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FormLabel className="text-[12px] text-[#262626]">Sorting</FormLabel>
        <RadioGroup
          row
          value={sortingOption}
          onChange={(e) =>
            onUpdateSortingOption(e.target.value as LineSortingOption)
          }
        >
          <FormControlLabel
            value="original"
            control={<Radio size="small" />}
            label="Keep original order"
          />
          <FormControlLabel
            value="manual"
            control={<Radio size="small" />}
            label="Manual Order"
          />
        </RadioGroup>
      </Box>

      {/* Line Customization */}
      <Accordion
        defaultExpanded
        sx={{ boxShadow: "none", border: "1px solid #eee" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Line Customization
            </Typography>
            <Chip
              label={`${visibleLinesCount}/${lines.length} visible`}
              size="small"
              color={visibleLinesCount === lines.length ? "default" : "primary"}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <HPButton
                size="small"
                variant="outlined"
                onClick={onShowAllLines}
                disabled={visibleLinesCount === lines.length}
              >
                Show All
              </HPButton>
              <HPButton
                size="small"
                variant="outlined"
                onClick={onHideAllLines}
                disabled={visibleLinesCount === 0}
              >
                Hide All
              </HPButton>
            </Box>

            <Divider />

            {/* Line list with DND */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleLineDragEnd}
            >
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {orderedLines.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No lines found
                  </Typography>
                ) : (
                  <SortableContext
                    items={lineOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedLines.map((line) => {
                      if (!line) return null; // Skip if line is undefined
                      const isHidden = hiddenLines[line.id] === false;
                      const hasCustomLabel = !!customLineLabels[line.id];
                      const displayLabel =
                        customLineLabels[line.id] || line.name;
                      const isEditing = editingLineId === line.id;

                      return (
                        <SortableLineItem
                          key={line.id}
                          item={line}
                          isHidden={isHidden}
                          hasCustomLabel={hasCustomLabel}
                          displayLabel={displayLabel}
                          isEditing={isEditing}
                          editValue={editLineValue}
                          isDragEnabled={sortingOption === "manual"}
                          swatchColor={lineSwatchColors?.[line.id]}
                          onToggleVisibility={onToggleLineVisibility}
                          onStartEdit={handleStartEditLine}
                          onSaveEdit={handleSaveLineEdit}
                          onCancelEdit={handleCancelLineEdit}
                          onResetLabel={handleResetLineLabel}
                          onEditValueChange={setEditLineValue}
                        />
                      );
                    })}
                  </SortableContext>
                )}
              </Box>
            </DndContext>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default LineChartCustomizationPanel;
